import { ChatMessage, MessageRole, SystemMessagePrefix } from "../../../shared/models";
import clsx from "clsx";
import { Busy } from "../components/busy";
import { parseMarkdown } from "../../../shared/helpers/markdown";
import { useCallback, useContext, useState } from "react";
import { BaseContext } from "../../../shared/base-context";
import { Button } from "../../../catalyst-components/button";
import { Divider } from "../../../catalyst-components/divider";

interface UseMessageBodyProps {
    userName: string;
    responseInProgress: boolean;
    debug: boolean;
}

interface DebugTraceEntry {
    type: string;
    tool_name?: string;
    tool_args?: any;
    result?: any;
    message?: string;
    timestamp: string;
}

// Debug trace display component (same as evergreen chat)
const DebugTraceDisplay: React.FC<{ trace: DebugTraceEntry[] }> = ({ trace }) => {
    const [expanded, setExpanded] = useState(false);

    const formatTimestamp = (ts: string) => {
        const date = new Date(ts);
        return date.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            fractionalSecondDigits: 3
        });
    };

    const getEntryIcon = (type: string) => {
        switch (type) {
            case "tool_call": return "→";
            case "tool_result": return "←";
            case "decision": return "•";
            default: return "·";
        }
    };

    return (
        <section className="bg-primary-2 p-4 mt-4 rounded">
            <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-sm text-primary-5">Agent Debug Trace</span>
                <Button color="light" className="text-xs py-1 px-2" onClick={() => setExpanded(!expanded)}>
                    {expanded ? "Collapse" : "Expand"} ({trace.length} steps)
                </Button>
            </div>

            {expanded && (
                <>
                    <Divider className="my-2" />
                    <div className="space-y-2 text-sm">
                        {trace.map((entry, index) => (
                            <div key={index} className="border-l-2 border-primary-3 pl-3">
                                <div className="flex items-start gap-2">
                                    <span className="text-primary-4">{getEntryIcon(entry.type)}</span>
                                    <span className="font-mono text-xs text-primary-4">
                                        [{formatTimestamp(entry.timestamp)}]
                                    </span>
                                    <span className="font-semibold capitalize text-primary-5">{entry.type.replace("_", " ")}</span>
                                </div>

                                {entry.type === "tool_call" && (
                                    <div className="ml-6 mt-1">
                                        <span className="font-mono bg-primary-3 px-1 rounded text-primary-5">{entry.tool_name}</span>
                                        {entry.tool_args && Object.keys(entry.tool_args).length > 0 && (
                                            <pre className="text-xs bg-primary-3 p-2 mt-1 rounded overflow-x-auto text-primary-5">
                                                {JSON.stringify(entry.tool_args, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                )}

                                {entry.type === "tool_result" && (
                                    <div className="ml-6 mt-1">
                                        <span className="text-xs text-primary-4">Result from </span>
                                        <span className="font-mono bg-primary-3 px-1 rounded text-xs text-primary-5">{entry.tool_name}</span>
                                        {entry.result && (
                                            <pre className="text-xs bg-primary-3 p-2 mt-1 rounded overflow-x-auto max-h-32 overflow-y-auto text-primary-5">
                                                {JSON.stringify(entry.result, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                )}

                                {entry.type === "decision" && entry.message && (
                                    <div className="ml-6 mt-1 text-primary-4 italic">
                                        {entry.message}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {!expanded && (
                <div className="text-xs text-primary-4">
                    {trace.filter(t => t.type === "tool_call").length} tool calls •
                    {" "}{trace.filter(t => t.type === "decision").length} decisions
                </div>
            )}
        </section>
    );
};

export const useMessageBody = ({ userName, responseInProgress, debug }: UseMessageBodyProps) => {
    const renderMessageBody = useCallback((message: ChatMessage) => {
        const isUser = message.role === MessageRole.User;
        let containerClass = clsx(isUser ? "ml-auto" : "mr-auto", "w-full lg:w-2/3 mb-4");
        if (debug && message.role === MessageRole.System) {
            containerClass = clsx(containerClass, "bg-primary-3 p-8 text-primary-1");
        }
        const messageClass = clsx(isUser ? "text-right" : "text-left", "font-extrabold");
        const messageBodyClass = clsx(isUser ? "text-right" : "text-left");
        
        return (
            <article className={containerClass}>
                {message.role !== MessageRole.System && (
                    <div className={messageClass}>
                        {message.role === MessageRole.User ? userName : 'Gruntled'}
                    </div>
                )}
                {message.role === MessageRole.System && renderSystemMessage(message)}
                {message.role !== MessageRole.System && (
                    <>
                        {!message.isLastMessage && (
                            <>
                                {!message.content && <Busy />}
                                {message.content && (
                                    <div
                                        className={messageBodyClass}
                                        dangerouslySetInnerHTML={{
                                            __html: message.content.indexOf(SystemMessagePrefix) === -1 && !isUser
                                                ? parseMarkdown(message.content)
                                                : message.content
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
                
                {/* Debug trace - only show for assistant messages when debug mode is on */}
                {debug && !isUser && message.debug_trace && message.debug_trace.length > 0 && (
                    <DebugTraceDisplay trace={message.debug_trace} />
                )}
            </article>
        );
    }, [userName, responseInProgress, debug]);

    return { renderMessageBody };
};

export const renderSystemMessage = (message: ChatMessage) => {
    let content = message.content;
    let isJSON = false;
    try {
        content = JSON.parse(message.content);
        isJSON = true;
    } catch (error) {
        isJSON = false;
    }
    return (
        <>
            <div className="text-center font-extrabold">
                System Message
            </div>
            {isJSON ? (
                <pre className="text-left text-wrap bg-primary-3 p-4 text-primary-1">
                    {JSON.stringify(content, null, 2)}
                </pre>
            ) : (
                <div dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }} />
            )}
        </>
    );
};
